import { ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, Input, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ClientOrderItemVm } from '../../models/client-order.view-model';
import { ClientOrderStore } from '../../client-order.store';
import { formatAmount, formatAmountInput } from '../../../../../utils/format-amount';

/**
 * One node of a snapshotted position tree (bom-tree-node look): every saved node's amount
 * is editable by double-click — the ordered quantity on a root, per-one-parent on a child —
 * and the right-click menu customizes the tree. Talks to the page's store directly, like
 * bom-tree-node, rather than re-emitting outputs up every nesting level.
 *
 * A frozen position (one with an allocation — production already copied its tree) keeps
 * only the root amount editable; children and the context menu are off until unlinked.
 */
@Component({
  selector: 'client-order-tree-node',
  templateUrl: './client-order-tree-node.component.html',
  styleUrl: './client-order-tree-node.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  host: {
    '[style.margin-left.px]': 'marginLeftPx',
  }
})
export class ClientOrderTreeNodeComponent implements OnInit {
  @Input({ required: true }) item!: ClientOrderItemVm;
  @Input() depthLevel = 0;
  @Input() frozen = false;

  // double-click edits, like the BOM tree node
  readonly editing = signal(false);
  readonly amountInput = viewChild<ElementRef<HTMLInputElement>>('amountInput');

  marginLeftPx = 0;

  formatAmount = formatAmount;
  formatAmountInput = formatAmountInput;

  constructor(
    private store: ClientOrderStore,
    private destroyRef: DestroyRef
  ) {
    // the input only exists while editing — focus it as soon as it appears (overseer report-input precedent)
    effect(() => {
      const input = this.amountInput();

      if (input) {
        input.nativeElement.focus();
        input.nativeElement.select();
      }
    });
  }

  // a placeholder awaiting the backend has no id yet and cannot be edited or customized
  get saved(): boolean {
    return this.item.data.clientOrderItemId > 0;
  }

  // on a frozen position the root amount (demand) may still change — never below what is allocated, the backend says
  get editable(): boolean {
    return this.saved && (!this.frozen || this.depthLevel === 0);
  }

  get customizable(): boolean {
    return this.saved && !this.frozen;
  }

  ngOnInit(): void {
    this.initMargin();
  }

  startEditing(): void {
    if (this.editable) {
      this.editing.set(true);
    }
  }

  stopEditing(): void {
    this.editing.set(false);
  }

  save(value: string): void {
    this.stopEditing();
    this.store.updateAmount(this.item.id, value).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  onContextMenu(event: MouseEvent): void {
    // native context menu stays available inside the edit input (copy/paste)
    if (this.editing() || !this.customizable) {
      return;
    }

    /**
     * Right-click at (almost) the same point again: close our menu and let the native one through.
     * The menu's top-left corner sits exactly under the opening click, so a repeated click a few px
     * up/left hits the row, not the panel — without the tolerance the bypass would depend on which
     * side of the corner the click lands.
     */
    const openAt = this.store.state.contextMenu?.position;
    const samePoint = openAt
      && Math.abs(openAt.x - event.clientX) < 10
      && Math.abs(openAt.y - event.clientY) < 10;

    if (samePoint) {
      this.store.closeContextMenu();
      return;
    }

    event.preventDefault();

    this.store.openContextMenu(this.item.id, { x: event.clientX, y: event.clientY });
  }

  private initMargin(): void {
    if (this.depthLevel === 0) {
      return;
    }

    this.marginLeftPx = 20;
  }
}
